import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeadFormStore } from '../../store/leadFormStore';
import { useProjectSegments } from '../../hooks/useProjectSegments';
import  ProgressIndicator  from './ProgressIndicator';
import FormNavigation from './FormNavigation';
import ParentInfoStep from './steps/ParentInfoStep';
import AddressStep from './steps/AddressStep';
import ChildrenStep from './steps/ChildrenStep';
import ScheduleStep from './steps/ScheduleStep';
import DocumentsStep from './steps/DocumentsStep';
import ReviewStep from './steps/ReviewStep';

const LeadForm: React.FC = () => {
  const navigate = useNavigate();
  const { progress, projectId, loadFromStorage, nextStep, previousStep, resetForm } = useLeadFormStore();
  const { segments } = useProjectSegments(projectId);

  // Define the steps
  const steps = ['parent', 'address', 'children', 'schedule', 'documents', 'review'];
  const currentStepIndex = steps.indexOf(progress.currentStep);

  // Load saved form data on mount
  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  const renderStep = () => {
    switch (progress.currentStep) {
      case 'parent':
        return <ParentInfoStep onNext={nextStep} onBack={previousStep} />;
      case 'address':
        return <AddressStep onNext={nextStep} onBack={previousStep} />;
      case 'children':
        return <ChildrenStep onNext={nextStep} onBack={previousStep} />;
      case 'schedule':
        return <ScheduleStep onNext={nextStep} onBack={previousStep} />;
      case 'documents':
        return <DocumentsStep onNext={nextStep} onBack={previousStep} />;
      case 'review':
        return <ReviewStep onNext={nextStep} onBack={previousStep} />;
      default:
        return <ParentInfoStep onNext={nextStep} onBack={previousStep} />;
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All progress will be lost.')) {
      resetForm();
      navigate('/leads');
    }
  };

  const getValidationMessage = () => {
    const { formData } = useLeadFormStore.getState();
    switch (progress.currentStep) {
      case 'parent':
        return 'Complete all parent information fields to continue';
      case 'address':
        return 'Complete all address fields to continue';
      case 'children':
        return segments.length > 0 && !formData.schoolSegmentId
          ? 'Select a school / campus and add at least one child to continue'
          : 'Add at least one child to continue';
      case 'documents':
        return 'Upload required documents to continue';
      default:
        return '';
    }
  };

  const validateCurrentStep = () => {
    const { formData } = useLeadFormStore.getState();
    
    switch (progress.currentStep) {
      case 'parent':
        const parent = formData.parent || {};
        return !!(
          parent.firstName?.trim() &&
          parent.lastName?.trim() &&
          parent.email?.trim() &&
          parent.phone?.trim()
        );
        
      case 'address':
        const address = formData.address || {};
        return !!(
          address.street?.trim() &&
          address.city?.trim() &&
          address.state?.trim() &&
          address.zipCode?.trim()
        );
        
      case 'children':
        const children = formData.children || [];
        // On multi-school projects, the family's campus is required so the
        // confirmation email and scheduling target the correct school.
        if (segments.length > 0 && !formData.schoolSegmentId) return false;
        return children.length > 0 && children.every(child =>
          child.firstName?.trim() &&
          child.dateOfBirth &&
          child.grade
        );
        
      case 'schedule':
        // Schedule is optional
        return true;
        
      case 'documents':
        const docs = formData.documents || {};
        return !!(
          docs.interestForm?.length > 0 &&
          docs.screenshot?.length > 0
        );
        
      case 'review':
        return true;
        
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Form Container with extra padding at bottom */}
        <div className="bg-white rounded-lg shadow-lg p-6 pb-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Create New Lead</h1>
            <p className="mt-2 text-sm text-gray-600">
              Complete all steps to add a new lead to the system
            </p>
          </div>

          <ProgressIndicator />

          {/* Step Content with margin bottom */}
          <div className="mt-8 mb-8">
            {renderStep()}
          </div>

          {/* Navigation with top margin and padding - hidden for review step */}
          {progress.currentStep !== 'review' && (
            <div>
              <FormNavigation
                currentStep={currentStepIndex + 1}
                totalSteps={steps.length}
                onNext={nextStep}
                onBack={previousStep}
                onCancel={handleCancel}
                nextLabel={currentStepIndex === steps.length - 1 ? 'Submit Lead' : 'Next'}
                isSubmitting={false}
                canGoNext={validateCurrentStep()}
                validationMessage={getValidationMessage()}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadForm;
